import { DragEvent } from 'react'
import { create } from 'zustand'
import { ChangeEvent } from 'react'
import { parseFilesFromEvent } from '@/helpers/parse-files-from-event'
import {
  acceptedFileTypes,
  type AcceptedFileTypes,
} from '@/const/acceptedFileTypes'
import { v4 as uuidv4 } from 'uuid'
export interface FileWithPathAndId extends File {
  path?: string
  fileId: string
}
interface DocumentUploadState {
  files: FileWithPathAndId[]
  invalidFiles: FileWithPathAndId[]
  clearUploadError: () => void
  setFiles: ({
    e,
  }: {
    e: ChangeEvent<HTMLInputElement> | DragEvent<HTMLDivElement>
  }) => void
  setLoadingFiles: (status: boolean) => void
  loadingFiles: boolean
  uploadError: boolean
  clearFiles: () => void
  removeInvalidFile: (pathToRemove: string) => void
  dropTarget: string | null
  setDropTarget: (target: string | null) => void
}

const useDocumentUploadStore = create<DocumentUploadState>((set, get) => ({
  dropTarget: null,
  setDropTarget: target => {
    set({ dropTarget: target })
  },
  files: [],
  invalidFiles: [],
  setFiles: ({ e }) => {
    if (e.type === 'drop' && e.persist) {
      e.persist()
    }

    Promise.resolve(parseFilesFromEvent(e))
      .then(files => {
        if (!files) {
          throw new Error('No files found')
        }
        const filesWithId: FileWithPathAndId[] = files.map(file => {
          Object.defineProperty(file, 'fileId', {
            value: uuidv4(),
          })
          return file as FileWithPathAndId
        })
        const invalidFiles = filesWithId.filter(
          file =>
            file.size > 39999999 ||
            !acceptedFileTypes.includes(
              (file.name.split('.').pop()?.toLowerCase() ??
                '') as AcceptedFileTypes,
            ),
        )

        const validFiles = filesWithId.filter(
          file => !invalidFiles.some(({ path }) => file.path === path),
        )

        set({
          files: validFiles,
          loadingFiles: false,
          invalidFiles,
        })
      })
      .catch(e => {
        console.error(e)
        set({ uploadError: true, loadingFiles: false })
      })
  },
  clearFiles: () => set({ files: [], invalidFiles: [] }),
  clearUploadError: () => set({ uploadError: false }),
  setLoadingFiles: status => set({ loadingFiles: status }),
  removeInvalidFile: fileIdToRemove => {
    const { invalidFiles } = get()

    const updatedFiles = invalidFiles.filter(
      ({ fileId }: { fileId: string }) => fileIdToRemove !== fileId,
    )
    set(() => ({
      invalidFiles: updatedFiles,
    }))
  },
  uploadError: false,
  loadingFiles: true,
}))

export default useDocumentUploadStore
